import React from "react";
import logoBase64 from "../../prescricoes/services/logoBase64";
import { gerarPDFTCLE, CONTATO_TCLE, SERVICO_TCLE } from "../services/gerarTCLE";
import { Paciente } from "../../../types";

// ============================================
// 🪪 PÁGINA PÚBLICA DE TRANSPARÊNCIA E PRIVACIDADE (B240, AI_108)
// ============================================
// Página PÚBLICA (rota /transparencia, acessível sem login) voltada ao PACIENTE
// e ao público geral, em cumprimento à transparência da Resolução CFM 2.454/2026
// (§1 — endereço de transparência) e à LGPD. Exposição ENXUTA por decisão de
// Leonardo (AI_108): apenas o que é voltado ao paciente — os documentos internos
// extensos (governança CFM, AIA, model card, política de segurança) são o desenho
// regulatório do sistema e NÃO são publicados aqui. O texto é fiel à Parte A do
// TCLE (docs/tcle-e-revisao-humana.md) e à §6 da governança.

const COR_VERDE = "#2d6a2e";

const cardSecao: React.CSSProperties = {
  background: "white",
  borderRadius: "14px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
  padding: "20px 22px",
  marginBottom: "16px",
};

const tituloSecao: React.CSSProperties = {
  fontSize: "17px",
  fontWeight: 700,
  color: COR_VERDE,
  margin: "0 0 8px 0",
};

const paragrafo: React.CSSProperties = {
  fontSize: "14.5px",
  lineHeight: 1.6,
  color: "#333",
  margin: "0 0 8px 0",
};

export default function TransparenciaPage() {
  const voltarAoLogin = () => {
    window.location.href = "/";
  };

  const verTCLE = () => {
    // Termo em branco (modelo de leitura) — sem paciente associado.
    gerarPDFTCLE({ nome: "" } as Paciente);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #2563eb 0%, #1e40af 100%)",
        padding: "32px 16px",
        fontFamily: "'Helvetica Neue', 'Segoe UI', Arial, sans-serif",
      }}
    >
      <div style={{ maxWidth: "820px", margin: "0 auto" }}>
        {/* CABEÇALHO */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            marginBottom: "20px",
          }}
        >
          <img
            src={logoBase64}
            alt="Logo"
            style={{ width: "64px", height: "64px", objectFit: "contain" }}
          />
          <div>
            <h1
              style={{
                color: "white",
                fontSize: "26px",
                fontWeight: 800,
                margin: 0,
                lineHeight: 1.2,
              }}
            >
              Transparência e Privacidade
            </h1>
            <p style={{ color: "#dbeafe", fontSize: "14px", margin: "4px 0 0" }}>
              {SERVICO_TCLE}
            </p>
          </div>
        </div>

        {/* INTRO */}
        <div style={cardSecao}>
          <p style={{ ...paragrafo, margin: 0 }}>
            Este serviço usa um programa de <strong>inteligência artificial (IA)
            de apoio à decisão</strong> para ajudar o seu médico. Esta página
            explica, de forma clara, como a IA é usada, como os seus dados são
            tratados e quais são os seus direitos.
          </p>
        </div>

        {/* 1. COMO A IA É USADA */}
        <div style={cardSecao}>
          <h2 style={tituloSecao}>Como a inteligência artificial é usada</h2>
          <p style={paragrafo}>
            A IA é uma <strong>ferramenta de apoio ao seu médico</strong> — ela
            ajuda a organizar informações, sugerir análises e redigir laudos.
          </p>
          <p style={paragrafo}>
            <strong>A IA não decide nada sozinha sobre o seu tratamento.</strong>{" "}
            Toda informação gerada por ela é{" "}
            <strong>revisada, editada e aprovada pelo seu médico</strong> antes
            de ter qualquer efeito no seu cuidado. Nenhuma conduta (diagnóstico,
            prescrição, pedido de exame) é tomada automaticamente pelo
            computador.
          </p>
        </div>

        {/* 2. REVISÃO HUMANA */}
        <div style={cardSecao}>
          <h2 style={tituloSecao}>
            Você sempre tem um médico revisando (Art. 20 da LGPD)
          </h2>
          <p style={{ ...paragrafo, margin: 0 }}>
            A lei garante que decisões que afetam você não sejam tomadas{" "}
            <strong>apenas</strong> por um sistema automático. Aqui isso é a
            regra de funcionamento: <strong>há sempre um médico revisando</strong>{" "}
            a saída da IA antes de qualquer registro no seu prontuário. Você pode
            pedir ao médico esclarecimentos sobre como uma informação apoiada por
            IA foi avaliada.
          </p>
        </div>

        {/* 3. DADOS E PRIVACIDADE */}
        <div style={cardSecao}>
          <h2 style={tituloSecao}>Os seus dados e a sua privacidade</h2>
          <p style={paragrafo}>
            <strong>Dados usados:</strong> seus dados de identificação e de saúde
            (prontuário, exames, sorologias), estritamente para apoiar a sua
            assistência clínica em hepatologia/transplante. Sempre que possível,
            os dados enviados à IA são <strong>minimizados/anonimizados</strong>.
          </p>
          <p style={{ ...paragrafo, margin: 0 }}>
            O banco de dados principal do prontuário fica <strong>no Brasil</strong>.
            Para gerar a análise de IA, alguns dados podem ser processados por
            serviços em nuvem no exterior, sob cláusulas de proteção e{" "}
            <strong>sem uso dos seus dados para treinar os modelos</strong>.
            Quando é necessário enviar dados que identifiquem você, isso ocorre{" "}
            <strong>com o seu consentimento</strong>.
          </p>
        </div>

        {/* 4. SEUS DIREITOS */}
        <div style={cardSecao}>
          <h2 style={tituloSecao}>Os seus direitos (LGPD)</h2>
          <p style={{ ...paragrafo, margin: 0 }}>
            Você pode, a qualquer momento: confirmar que há tratamento de dados,
            acessar seus dados, corrigir informações, pedir esclarecimentos e{" "}
            <strong>revogar o seu consentimento</strong>. A revogação suspende
            novos usos de IA no seu atendimento e <strong>não prejudica</strong>{" "}
            a continuidade do seu cuidado médico, nem apaga registros que a lei
            obriga a manter (o prontuário é guardado por 20 anos — Resolução CFM
            1.821/2007).
          </p>
        </div>

        {/* 5. TCLE */}
        <div style={cardSecao}>
          <h2 style={tituloSecao}>Termo de Consentimento (TCLE)</h2>
          <p style={paragrafo}>
            O uso da IA no seu atendimento é registrado em um Termo de
            Consentimento Livre e Esclarecido. Você pode ler o modelo do termo
            abaixo.
          </p>
          <button
            onClick={verTCLE}
            style={{
              padding: "11px 22px",
              background: COR_VERDE,
              color: "white",
              border: "none",
              borderRadius: "10px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            📄 Ler o Termo de Consentimento (TCLE)
          </button>
        </div>

        {/* 6. CONTATO */}
        <div style={cardSecao}>
          <h2 style={tituloSecao}>Contato</h2>
          <p style={{ ...paragrafo, margin: 0 }}>
            Para exercer os seus direitos ou tirar dúvidas sobre o tratamento dos
            seus dados, fale com o responsável:{" "}
            <a
              href={`mailto:${CONTATO_TCLE}`}
              style={{ color: COR_VERDE, fontWeight: 600 }}
            >
              {CONTATO_TCLE}
            </a>
            .
          </p>
        </div>

        {/* VOLTAR */}
        <div style={{ textAlign: "center", marginTop: "8px" }}>
          <button
            onClick={voltarAoLogin}
            style={{
              padding: "11px 28px",
              background: "rgba(255,255,255,0.2)",
              color: "white",
              border: "1px solid rgba(255,255,255,0.4)",
              borderRadius: "10px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            ← Voltar ao login
          </button>
        </div>

        <p
          style={{
            textAlign: "center",
            color: "#dbeafe",
            fontSize: "12px",
            marginTop: "20px",
          }}
        >
          Sistema em conformidade com a LGPD e a Resolução CFM 2.454/2026.
        </p>
      </div>
    </div>
  );
}
